import { Button } from '@ordr-as/umami-react/button';
import React, { useEffect, useState } from 'react';
import { getInitalStateForDynamicUnintegratedPaymentFields } from '../../../../../utils/payment';
import { useScreenSize } from '../../../../../context/ScreenSizeContext';
import { useIntl } from 'react-intl';
import BackButton from '../../../../../components/BackButton';
import FormattedMessage from '../../../../../components/FormattedMessage';
import CloseIcon from '@ordr-as/icons/CloseIcon';
import WarningIcon from '@ordr-as/icons/WarningIcon';
import CheckoutUnintegratedPaymentFieldsFavritGiftCard from './CheckoutUnintegratedPaymentFieldsFavritGiftCard';
import { useDebounce } from 'use-debounce';
import { useGiftCardSearch } from '../../../../../hooks/useGiftCards';
import { useLocation } from '../../../../../context/LocationContext';
import classNames from 'classnames';
import { useCheckout } from '../../../../../context/CheckoutContext';
import { useServiceCharge, useTotalPrice } from '../../../../../hooks/useTotalPrice';
import { PaymentMethodDto } from '../../../../../../generated-api';

interface Props {
  paymentMethod: PaymentMethodDto;
  onNavigateBack: () => void;
  onSubmit: (dynamicFields: { [key: string]: string }) => void;
  isOpen: boolean;
}

const CheckoutUnintegratedPaymentFavritGiftCard = (props: Props) => {
  const { paymentMethod, onNavigateBack, onSubmit } = props;
  const initalStateForDynamicFields = getInitalStateForDynamicUnintegratedPaymentFields(paymentMethod);
  const [dynamicFieldValues, setDynamicFieldValues] = useState(initalStateForDynamicFields);
  const dynamicFields = paymentMethod.unintegratedPaymentFields;
  const requiredFields = dynamicFields?.filter((field) => field.required);
  const { isPhone } = useScreenSize();
  const intl = useIntl();
  const { location, formatCurrency } = useLocation();
  const { deliveryFee, deliveryMethod } = useCheckout();
  const discountedTotalPrice = useTotalPrice();
  const giftCardSource = paymentMethod.paymentMethod === 'FAVRIT_GIFT_CARD_INTERNAL' ? 'INTERNAL' : 'EXTERNAL';
  const [reference, setReference] = useDebounce<string>('', 300);
  const { data: giftCardSearchResponse } = useGiftCardSearch(location.id, reference, giftCardSource);
  const { giftCard, valid } = giftCardSearchResponse || {};
  const totalServiceCharge = useServiceCharge(deliveryMethod);

  const amountToPay = discountedTotalPrice + deliveryFee + totalServiceCharge;

  useEffect(() => {
    if (giftCard && valid && amountToPay <= giftCard.balance) {
      setDynamicFieldValues((prevState) => ({ ...prevState, reference: giftCard.id }));
      return;
    }

    setDynamicFieldValues((prevState) => ({ ...prevState, reference: '' }));
  }, [giftCard, valid, amountToPay, setDynamicFieldValues]);

  const canSubmit = !requiredFields?.map((requiredField) => dynamicFieldValues[requiredField.name]).includes('');

  const handleCompletePurchase = () => {
    if (canSubmit) {
      onSubmit(dynamicFieldValues);
      onNavigateBack();
    }
  };

  const giftCardClassnames = classNames('flex flex-col border-2 rounded p-2 tablet:p-4 gap-2 tablet:gap-4 text-sm', {
    'bg-orange-lightest border-orange-darkest': !valid,
    'bg-green-lightest border-green-dark': valid,
  });

  const amountToPayExceedsBalance = giftCard && valid && amountToPay > giftCard.balance;

  return (
    <>
      {!isPhone && <BackButton navigateTo={onNavigateBack} />}
      <div className="p-4 tablet:p-6 h-main">
        <div className="flex flex-row w-full pb-4">
          <h3 className="basis-5/6 mb-2">
            {paymentMethod.label ??
              intl.formatMessage({ id: 'payment.paymentMethods.unintegratedFieldsDialog.title.fallback' })}
          </h3>
          {isPhone && (
            <Button className="basis-1/6" onClick={onNavigateBack}>
              <CloseIcon />
            </Button>
          )}
        </div>
        <CheckoutUnintegratedPaymentFieldsFavritGiftCard
          dynamicFields={dynamicFields}
          autoFocus={false}
          dynamicFieldValues={dynamicFieldValues}
          setDynamicFieldValues={setDynamicFieldValues}
          setReference={setReference}
          giftCardSource={giftCardSource}
        />
        {giftCard && (
          <div className={giftCardClassnames}>
            <span className="text-bold break-words">Gift card reference: {giftCard.reference}</span>
            <div className="flex justify-between">
              <div className="flex flex-col">
                <span className="text-grey-dark">Active from</span>
                <span>{giftCard.activeFrom.slice(0, 10)}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-grey-dark">Active to</span>
                <span>{giftCard.activeTo.slice(0, 10)}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-grey-dark">Value</span>
                <span>{formatCurrency(giftCard.deposit)}</span>
              </div>
            </div>
            <span className="text-bold">Available balance: {formatCurrency(giftCard.balance)}</span>
            {!valid && (
              <div className="flex items-center gap-1 text-bold">
                <WarningIcon className="h-4 w-4" />
                <span>Gift card is not valid</span>
              </div>
            )}
          </div>
        )}
        {amountToPayExceedsBalance && (
          <div className="flex flex-col bg-yellow-lightest rounded mt-2 tablet:mt-4 p-2 tablet:p-4 text-sm gap-2">
            <div className="flex items-center">
              <WarningIcon className="h-4 w-4" />
              <span className="ml-1 text-bold">Balance too low</span>
            </div>
            <span>Gift card balance is less than amount for this payment.</span>
          </div>
        )}
      </div>
      <div className="fixed w-full tablet:w-[33%] bottom-0 bg-brand-white shadow-[15px_-15px_50px_-19px_rgb(0,0,0,15%)] py-4 px-4 tablet:px-6">
        <Button onClick={handleCompletePurchase} fullWidth kind="primary" isDisabled={!canSubmit}>
          <FormattedMessage id="payment.paymentMethods.checkoutUnintegratedPayment.submitAction" />
        </Button>
      </div>
    </>
  );
};

export default CheckoutUnintegratedPaymentFavritGiftCard;
